import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  EnvelopeSimple, Buildings, Palette, ShieldStar, PaperPlaneTilt, X, Clock,
  Users as UsersIcon, PencilSimple, SignOut, Trash,
} from "@phosphor-icons/react";

export default function Settings() {
  const { currentOrg, user, refresh } = useAuth();
  const isAdmin = currentOrg?.role === "admin";
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [sending, setSending] = useState(false);

  // Rename
  const [renameDraft, setRenameDraft] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  useEffect(() => { setRenameDraft(currentOrg?.name || ""); }, [currentOrg?.name]);

  // Members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [busyMember, setBusyMember] = useState(null);

  const loadInvites = useCallback(async () => {
    if (!isAdmin) { setInvites([]); return; }
    setLoadingInvites(true);
    try { setInvites((await api.get("/invitations")).data); } catch { setInvites([]); }
    finally { setLoadingInvites(false); }
  }, [isAdmin, currentOrg?.org_id]);

  const loadMembers = useCallback(async () => {
    if (!currentOrg?.org_id) return;
    setLoadingMembers(true);
    try { setMembers((await api.get("/organizations/members")).data); } catch { setMembers([]); }
    finally { setLoadingMembers(false); }
  }, [currentOrg?.org_id]);

  useEffect(() => { loadInvites(); loadMembers(); }, [loadInvites, loadMembers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) { toast.error("Enter an email to invite"); return; }
    setSending(true);
    try {
      await api.post("/invitations", { email, role: inviteRole });
      toast.success(`Invite sent to ${email}`);
      setInviteEmail(""); await loadInvites();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send invite"); }
    finally { setSending(false); }
  };

  const revokeInvite = async (inv) => {
    try {
      await api.delete(`/invitations/${inv.invite_id}`);
      setInvites((all) => all.filter((x) => x.invite_id !== inv.invite_id));
      toast.success(`Invite to ${inv.email} revoked`);
    } catch { toast.error("Could not revoke invite"); }
  };

  const saveRename = async () => {
    const name = renameDraft.trim();
    if (!name || name === currentOrg?.name) return;
    setSavingRename(true);
    try {
      await api.patch(`/organizations/${currentOrg.org_id}`, { name });
      toast.success("Workspace renamed");
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Rename failed"); }
    finally { setSavingRename(false); }
  };

  const removeMember = async (m) => {
    const isSelf = m.user_id === user?.user_id;
    const label = isSelf ? "leave this workspace" : `remove ${m.name || m.email}`;
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    setBusyMember(m.user_id);
    try {
      await api.delete(`/organizations/${currentOrg.org_id}/members/${m.user_id}`);
      if (isSelf) {
        toast.success("You've left this workspace");
        await refresh();
      } else {
        setMembers((all) => all.filter((x) => x.user_id !== m.user_id));
        toast.success(`Removed ${m.name || m.email}`);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove member"); }
    finally { setBusyMember(null); }
  };

  return (
    <div className="p-8 md:p-12 max-w-4xl" data-testid="settings-page">
      <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Settings</h1>
      <p className="mt-3 text-sm text-ink-secondary max-w-lg">
        Manage the current workspace: <span className="text-ink-primary">{currentOrg?.name || "—"}</span>
      </p>

      {/* Workspace details + rename */}
      <section className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8" data-testid="settings-workspace-card">
        <div className="flex items-center gap-2 text-ink-secondary text-xs uppercase tracking-widest">
          <Buildings weight="duotone" className="h-4 w-4" /> Workspace details
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="settings-org-name"
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                disabled={!isAdmin || savingRename}
                maxLength={120}
                className="bg-graphite-800 border-white/10 text-ink-primary"
              />
              {isAdmin && (
                <button
                  data-testid="settings-rename-save"
                  onClick={saveRename}
                  disabled={savingRename || !renameDraft.trim() || renameDraft.trim() === currentOrg?.name}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-2 text-xs font-medium hover:bg-neutral-100 disabled:opacity-50"
                >
                  <PencilSimple weight="bold" className="h-3 w-3" />
                  {savingRename ? "Saving…" : "Save"}
                </button>
              )}
            </div>
            {!isAdmin && <p className="text-[11px] text-ink-secondary/70">Only workspace admins can rename.</p>}
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Slug</label>
            <Input value={currentOrg?.slug || ""} disabled readOnly data-testid="settings-org-slug" className="bg-graphite-800 border-white/10 text-ink-primary" />
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary flex items-center gap-2">
              <Palette weight="duotone" className="h-4 w-4" /> Primary color
            </label>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-sm border border-white/10" style={{ backgroundColor: currentOrg?.primary_color || "#FFFFFF" }} />
              <Input value={currentOrg?.primary_color || "#FFFFFF"} disabled readOnly data-testid="settings-org-color" className="bg-graphite-800 border-white/10 text-ink-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary flex items-center gap-2">
              <ShieldStar weight="duotone" className="h-4 w-4" /> Your role
            </label>
            <Input value={currentOrg?.role || "member"} disabled readOnly data-testid="settings-org-role" className="bg-graphite-800 border-white/10 text-ink-primary capitalize" />
          </div>
        </div>
      </section>

      {/* Members list */}
      <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8" data-testid="settings-members-card">
        <div className="flex items-center gap-2 text-ink-secondary text-xs uppercase tracking-widest">
          <UsersIcon weight="duotone" className="h-4 w-4" /> Members
        </div>
        {loadingMembers ? (
          <div className="mt-3 text-sm text-ink-secondary">Loading members…</div>
        ) : members.length === 0 ? (
          <div className="mt-3 text-sm text-ink-secondary" data-testid="settings-members-empty">No members yet.</div>
        ) : (
          <ul className="mt-4 divide-y divide-white/5 border border-white/10 rounded-md" data-testid="settings-members-list">
            {members.map((m) => {
              const isSelf = m.user_id === user?.user_id;
              const canRemoveOther = isAdmin && !isSelf;
              return (
                <li key={m.user_id} data-testid={`settings-member-row-${m.user_id}`} className="px-4 py-3 flex items-center gap-3">
                  {m.picture ? (
                    <img src={m.picture} alt="" className="h-8 w-8 rounded-full object-cover border border-white/10" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-graphite-700 grid place-items-center text-xs">{(m.name || m.email || "U").charAt(0)}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink-primary truncate">
                      {m.name || m.email}
                      {isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-secondary/70">You</span>}
                    </div>
                    <div className="text-[11px] text-ink-secondary truncate">{m.email}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.role === "admin" ? "border-amber-400/30 bg-amber-400/5 text-amber-200" : "border-white/10 text-ink-secondary"}`}>
                    {m.role}
                  </span>
                  {isSelf ? (
                    <button
                      data-testid={`settings-member-leave-${m.user_id}`}
                      onClick={() => removeMember(m)}
                      disabled={busyMember === m.user_id}
                      title="Leave workspace"
                      className="inline-flex items-center gap-1 text-[11px] rounded-full border border-white/10 px-3 py-1 text-ink-secondary hover:text-red-300 hover:bg-white/5 disabled:opacity-60"
                    >
                      <SignOut weight="bold" className="h-3 w-3" />
                      Leave
                    </button>
                  ) : canRemoveOther ? (
                    <button
                      data-testid={`settings-member-remove-${m.user_id}`}
                      onClick={() => removeMember(m)}
                      disabled={busyMember === m.user_id}
                      title="Remove from workspace"
                      className="p-2 rounded-md text-ink-secondary hover:text-red-300 hover:bg-white/5 disabled:opacity-60"
                    >
                      <Trash weight="bold" className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <span className="w-8" aria-hidden />
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {isAdmin && (
          <p className="mt-4 text-[11px] text-ink-secondary/70">
            Admins can remove members. Every workspace must have at least one admin — remove/leave is blocked otherwise.
          </p>
        )}
      </section>

      {/* Invite Members */}
      <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8" data-testid="settings-invite-card">
        <div className="flex items-center gap-2 text-ink-secondary text-xs uppercase tracking-widest">
          <EnvelopeSimple weight="duotone" className="h-4 w-4" /> Invite members
        </div>
        {!isAdmin ? (
          <p className="mt-3 text-sm text-ink-secondary">Only workspace admins can invite new members.</p>
        ) : (
          <>
            <p className="mt-2 text-sm text-ink-secondary max-w-md">
              Send a secure single-use invite. The link expires in 7 days and lands the recipient directly in this workspace after Google sign-in.
            </p>
            <form onSubmit={handleInvite} className="mt-5 flex flex-col sm:flex-row gap-3">
              <Input
                data-testid="settings-invite-email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                type="email"
                placeholder="teammate@company.com"
                className="flex-1 bg-graphite-800 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
              />
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="settings-invite-role" className="sm:w-[140px] bg-graphite-800 border-white/10 text-ink-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-graphite-800 border-white/10 text-ink-primary">
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <button
                type="submit"
                data-testid="settings-invite-send"
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60 transition-colors"
              >
                <PaperPlaneTilt weight="bold" className="h-4 w-4" />
                {sending ? "Sending…" : "Send invite"}
              </button>
            </form>

            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Pending invites</div>
              {loadingInvites ? (
                <div className="mt-3 text-sm text-ink-secondary">Loading…</div>
              ) : invites.length === 0 ? (
                <div className="mt-3 text-sm text-ink-secondary/70" data-testid="settings-invites-empty">No pending invites.</div>
              ) : (
                <ul className="mt-3 divide-y divide-white/5 border border-white/10 rounded-md" data-testid="settings-invites-list">
                  {invites.map((inv) => (
                    <li key={inv.invite_id} className="px-4 py-3 flex items-center gap-3" data-testid={`settings-invite-row-${inv.invite_id}`}>
                      <EnvelopeSimple weight="duotone" className="h-4 w-4 text-ink-secondary" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-ink-primary truncate">{inv.email}</div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-secondary">
                          <span className="capitalize">{inv.role}</span>
                          <span className="opacity-40">·</span>
                          <span className="inline-flex items-center gap-1">
                            <Clock weight="regular" className="h-3 w-3" />
                            invited by {inv.invited_by_name || "an admin"}
                          </span>
                        </div>
                      </div>
                      <button
                        data-testid={`settings-invite-revoke-${inv.invite_id}`}
                        onClick={() => revokeInvite(inv)}
                        className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                        title="Revoke"
                      >
                        <X weight="bold" className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </section>

      <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6 md:p-8" data-testid="settings-account-card">
        <div className="text-ink-secondary text-xs uppercase tracking-widest">Signed in as</div>
        <div className="mt-3 flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-10 w-10 rounded-full border border-white/10 object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-graphite-700 grid place-items-center">{(user?.name || "U").charAt(0)}</div>
          )}
          <div>
            <div className="text-sm text-ink-primary">{user?.name}</div>
            <div className="text-xs text-ink-secondary">{user?.email}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
